import { Check, FileText, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type SubmissionPhaseDraft } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { Button } from "../../design/components/Button.js";
import { ResponsiveSheet } from "../../design/components/ResponsiveSheet.js";
import { PreviewFrame, type WorkspacePreviewProps } from "./PreviewFrame.js";
import { ReadinessItem, ZeroState } from "./previewPrimitives.js";

export function SubmissionWorkspacePreview({ conference, initialPhase }: WorkspacePreviewProps & { initialPhase?: SubmissionPhaseDraft | null }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<SubmissionPhaseDraft | null>(initialPhase ?? null);
  const [name, setName] = useState("Abstract submissions");
  const [opensOn, setOpensOn] = useState("");
  const [closesOn, setClosesOn] = useState("");
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialPhase !== undefined) { setPhase(initialPhase); return; }
    let active = true;
    api.submissionPhase(conference.id, conference.organization_id)
      .then((saved) => { if (active) setPhase(saved); })
      .catch((reason) => { if (active) setError(apiErrorMessage(reason, "The saved phase could not be loaded.")); });
    return () => { active = false; };
  }, [conference.id, conference.organization_id, initialPhase]);

  const beginEdit = () => {
    setName(phase?.name || "Abstract submissions");
    setOpensOn(phase?.opens_on || "");
    setClosesOn(phase?.closes_on || "");
    setInstructions(phase?.instructions || "");
    setMessage("");
    setError("");
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim() || !opensOn || !closesOn || !instructions.trim()) {
      setError("Complete the phase name, dates, and author instructions before saving.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api.saveSubmissionPhase(conference.id, conference.organization_id, {
        name: name.trim(),
        opens_on: opensOn,
        closes_on: closesOn,
        instructions: instructions.trim(),
      });
      setPhase(saved);
      setMessage("Phase draft saved");
      setOpen(false);
    } catch (reason) {
      setError(apiErrorMessage(reason, "The phase draft could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return <PreviewFrame
    title="Submission workflow"
    description="Prepare the author journey and save its private configuration before opening submissions."
    operationalNote="Submission phase drafts are saved securely for this conference. Opening the phase remains a separate action."
    action={<Button onClick={beginEdit}><Plus size={16} />Configure phase</Button>}
  >
    {message ? <div className="workspace-save-status" role="status"><Check size={17} aria-hidden="true" />{message}</div> : null}
    {error && !open ? <p className="form-error" role="alert">{error}</p> : null}
    <section className="submission-phase-line" aria-label="Submission phase timeline">{["Draft", "Open", "Revision", "Closed"].map((step, index) => <div className={index === 0 ? "is-current" : ""} key={step}><span>{index + 1}</span><strong>{step}</strong><small>{index === 0 ? phase?.name || "Configuration" : "Not scheduled"}</small></div>)}</section>
    <div className="submission-readiness-grid"><section className="workspace-panel" aria-label="Submission form readiness"><header><FileText size={19} /><div><h2>Submission form readiness</h2><p>What authors must provide.</p></div></header><ul className="readiness-list"><ReadinessItem title="Core details" detail="Title, abstract, and authors" /><ReadinessItem title="File requirements" detail="Types and size limits" /><ReadinessItem title="Custom questions" detail="No questions configured" /></ul></section><section className="workspace-panel" aria-label="Opening checklist"><h2>Opening checklist</h2><ul className="readiness-list"><ReadinessItem title="Author instructions" detail={phase?.instructions || "Not configured"} /><ReadinessItem title="Opening and closing dates" detail={phase ? `${phase.opens_on} – ${phase.closes_on} · ${conference.time_zone}` : conference.time_zone} /><ReadinessItem title="Eligibility" detail="All new submissions" /><ReadinessItem title="Author preview" detail="Required before opening" /></ul></section></div>
    <ZeroState title="0 submissions received" description="Submissions will appear here only after this saved phase is deliberately opened." />
    <ResponsiveSheet open={open} title="Configure submission phase" onClose={() => setOpen(false)} footer={<div className="preview-sheet-actions"><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} busy={saving}>Save phase draft</Button></div>}>
      <div className="preview-sheet-content"><p><strong>{conference.name}</strong></p><span>Dates use {conference.time_zone}. Saving does not open submissions.</span><div className="preview-field"><label htmlFor="submission-phase-name">Phase name</label><input id="submission-phase-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={160} /></div><div className="preview-field"><label htmlFor="submission-opens-on">Opening date</label><input id="submission-opens-on" type="date" value={opensOn} onChange={(event) => setOpensOn(event.target.value)} /></div><div className="preview-field"><label htmlFor="submission-closes-on">Closing date</label><input id="submission-closes-on" type="date" value={closesOn} min={opensOn || undefined} onChange={(event) => setClosesOn(event.target.value)} /></div><div className="preview-field"><label htmlFor="submission-instructions">Author instructions</label><textarea id="submission-instructions" rows={6} value={instructions} onChange={(event) => setInstructions(event.target.value)} maxLength={4000} /></div>{error ? <p className="form-error" role="alert">{error}</p> : null}</div>
    </ResponsiveSheet>
  </PreviewFrame>;
}
